"use client";

import { useCallback, useEffect, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useSatelliteNavigationOptional } from "@to/ui";

import { Container } from "./shell/Container";
import { AgentSearchBar } from "./agent-search-bar";
import { AgentFilters } from "./agent-filters";
import { AgentResults } from "./agent-results";
import { AgentResultImpressions } from "./agent-result-impressions";
import { DirectoryFilterTelemetry } from "./analytics/DirectoryFilterTelemetry";
import { agentDirectoryRouteTemplate } from "@/lib/observability/route-template";
import { AGENT_ORDER_SEED_PARAM, createAgentOrderSeed } from "@/lib/agent-order-seed";
import type { AgentCardAgent } from "./agent-card";
import type { AdAgentResultsPhase } from "@/lib/data/agents";
import {
  agentFilterToQuery,
  type AgentFilterState,
  type AgentFilterSubsets,
} from "./agent-types";

// A navigation entry describes the whole document, including later client
// navigations and component remounts. Only a client effect consumes this flag;
// no request, tenant, user or ordering data is kept at module scope.
let consumedDirectoryReload = false;

interface AgentsExplorerProps {
  /** Internal non-PII organization id used by fairness telemetry. */
  orgIdentifier: string;
  /** One tab-local results sequence; replaced on a full document refresh. */
  orderSeed: string;
  /** Dev path prefix (`/${slug}`); empty in subdomain prod. */
  basePath: string;
  /** Approved hub context; ordinary directory behavior remains the default. */
  resultsPath?: string;
  intro?: React.ReactNode;
  footer?: React.ReactNode;
  subsets: AgentFilterSubsets;
  /** Filter/sort/page state parsed from the URL by the server page. */
  filterState: AgentFilterState;
  agents: AgentCardAgent[];
  total: number;
  page: number;
  totalPages: number;
  pageSize: number;
  phase: AdAgentResultsPhase;
  phasePage: number;
  phaseOffset: number;
  journeyOffset?: number;
  featuredTotal: number;
  compactTotal: number;
}

/**
 * Client orchestrator for the SSR `/agents` page, laid out per the v1
 * reference: a full-bleed brand-gradient hero band ("Find Your Agent" + the
 * verified-REALTOR count), a white search card overlapping the band's bottom
 * edge (search input + selects + facet chips via `AgentFilters`), then the
 * results grid on the same background as the surrounding site.
 *
 * The URL query string is the single source of truth for filter/sort/page —
 * every control writes the next state into the URL (`router.push`), the server
 * re-renders with fresh data, and back/forward + SSR work for free. The search
 * input debounces inside `AgentSearchBar` before it ever reaches the URL, so
 * cache keys don't thrash.
 */
export function AgentsExplorer({
  orgIdentifier,
  orderSeed,
  basePath,
  resultsPath,
  intro,
  footer,
  subsets,
  filterState,
  agents,
  total,
  page,
  totalPages,
  pageSize,
  phase,
  phasePage,
  phaseOffset,
  journeyOffset,
  featuredTotal,
  compactTotal,
}: AgentsExplorerProps) {
  const router = useRouter();
  const pathname = usePathname();
  const navigation = useSatelliteNavigationOptional();

  const checkedNavigation = useRef(false);
  useEffect(() => {
    if (!checkedNavigation.current) {
      checkedNavigation.current = true;
      const entry = performance.getEntriesByType?.("navigation")[0] as PerformanceNavigationTiming | undefined;
      let documentPathname: string | undefined;
      try { documentPathname = entry?.name ? new URL(entry.name).pathname : undefined; } catch { /* No trustworthy document URL. */ }
      if (!consumedDirectoryReload && entry?.type === "reload" && documentPathname === pathname) {
        consumedDirectoryReload = true;
        const query = agentFilterToQuery(filterState, 1);
        query.set(AGENT_ORDER_SEED_PARAM, createAgentOrderSeed());
        router.replace(`${pathname}?${query}${window.location.hash}`, { scroll: false });
        return;
      }
    }
    // Stamp the initial history entry too: returning to page 1 must retain its
    // sequence even if Next's in-memory page cache has been evicted.
    const query = agentFilterToQuery(filterState, page);
    query.set(AGENT_ORDER_SEED_PARAM, orderSeed);
    const href = `${pathname}?${query}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", href);
  }, [filterState, page, pathname, orderSeed, router]);

  // Serialize (filterState, page) → URL query and navigate (the wire format lives
  // in `agentFilterToQuery`). `pathname` already carries the dev org prefix, so
  // the prefix is preserved across navigation (lesson 2026-03-30).
  const pushState = useCallback(
    (next: AgentFilterState, nextPage: number, trackNavigation = true) => {
      const query = agentFilterToQuery(next, nextPage);
      query.set(AGENT_ORDER_SEED_PARAM, orderSeed);
      const targetHref = `${pathname}?${query}`;
      const started = !trackNavigation ||
        (navigation?.beginNavigation({
          routeTemplate: "/agents",
          navigationType: "data_action",
          targetKey: targetHref,
          targetHref,
        }) ?? true);
      if (!started) return false;
      router.push(targetHref, { scroll: false });
      return true;
    },
    [navigation, router, pathname, orderSeed],
  );

  // Any filter/search/sort change resets to page 1; page changes keep filters.
  const onSearchChange = (q: string) => pushState({ ...filterState, q }, 1);
  const onFiltersChange = (next: AgentFilterState) => pushState(next, 1);
  const onPageChange = (nextPage: number) => {
    // The shell already starts link navigation in its capture listener.
    if (!pushState(filterState, nextPage, false)) return false;
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    return true;
  };

  const agentDetailPath = `${basePath}/agents`;
  const resultViewKey = `${resultsPath ?? agentDetailPath}?${agentFilterToQuery(filterState, page)}`;
  const featuredAgents = agents.filter((agent) => agent.isPro);
  const compactAgents = agents.filter((agent) => !agent.isPro);

  return (
    <div style={{ backgroundColor: "var(--brand-background)" }}>
      <DirectoryFilterTelemetry
        orgIdentifier={orgIdentifier}
        surface="agent_search"
        routeTemplate={agentDirectoryRouteTemplate(pathname, basePath.slice(1))}
        activeFilters={{ specialty_id: subsets.specialties.find((option) => option.value === filterState.specialtyId)?.value ?? "",
          language_id: subsets.languages.find((option) => option.value === filterState.languageId)?.value ?? "",
          service_area_id: subsets.serviceAreas.find((option) => option.value === filterState.serviceAreaId)?.value ?? "",
          has_open_houses: filterState.hasOpenHouses, has_reviews: filterState.hasReviews }}
        resultCount={total}
        sort={filterState.sort}
      />
      {/* ── Hero band — full-bleed brand gradient (v1) ─────────────────────── */}
      {intro ? <Container className="py-8">{intro}</Container> : <section
        className="py-8 text-center sm:py-10"
        style={{
          background:
            "linear-gradient(135deg, var(--brand-secondary), var(--brand-primary))",
        }}
      >
        <Container>
          <h1
            className="text-2xl font-bold tracking-tight sm:text-3xl"
            style={{ color: "var(--brand-primary-foreground)" }}
          >
            Find Your Agent
          </h1>
          <p
            className="mt-1 text-sm"
            style={{
              color:
                "color-mix(in srgb, var(--brand-primary-foreground) 70%, transparent)",
            }}
          >
            {total > 0
              ? `${total.toLocaleString("en-US")} board-verified ${total === 1 ? "REALTOR®" : "REALTORS®"}`
              : "Board-verified REALTORS®"}
          </p>
        </Container>
      </section>}

      {/* ── Search card — overlaps the hero's bottom edge (v1) ────────────── */}
      <Container className={intro ? "relative z-10" : "relative z-10 -mt-5"}>
        <div
          className="rounded-2xl border p-4"
          style={{
            backgroundColor: "var(--brand-card-bg)",
            borderColor: "var(--brand-card-border)",
            boxShadow: "var(--shadow-hover)",
          }}
        >
          <AgentFilters
            subsets={subsets}
            value={filterState}
            onChange={onFiltersChange}
            busy={navigation?.isPending ?? false}
            searchSlot={
              <AgentSearchBar
                value={filterState.q}
                onChange={onSearchChange}
                busy={navigation?.isPending ?? false}
              />
            }
          />
        </div>
      </Container>

      {/* ── Results ────────────────────────────────────────────────────────── */}
      <Container className="pb-12 pt-8">
        {featuredAgents.length > 0 && (
          <AgentResultImpressions
            orgIdentifier={orgIdentifier}
            agents={featuredAgents}
            phase="featured"
            globalPage={page}
            sort={filterState.sort}
            viewKey={resultViewKey}
          />
        )}
        {compactAgents.length > 0 && (
          <AgentResultImpressions
            orgIdentifier={orgIdentifier}
            agents={compactAgents}
            phase="compact"
            globalPage={page}
            sort={filterState.sort}
            viewKey={resultViewKey}
          />
        )}
        <AgentResults
          agents={agents}
          total={total}
          page={page}
          totalPages={totalPages}
          pageSize={pageSize}
          phase={phase}
          phasePage={phasePage}
          phaseOffset={phaseOffset}
          journeyOffset={journeyOffset}
          featuredTotal={featuredTotal}
          compactTotal={compactTotal}
          onPageChange={onPageChange}
          pageHref={(nextPage) => {
            const query = agentFilterToQuery(filterState, nextPage);
            query.set(AGENT_ORDER_SEED_PARAM, orderSeed);
            return `${resultsPath ?? agentDetailPath}?${query}`;
          }}
          resetHref={`${resultsPath ?? agentDetailPath}?${AGENT_ORDER_SEED_PARAM}=${orderSeed}`}
          agentDetailPath={agentDetailPath}
        />
        {footer}
      </Container>
    </div>
  );
}
